import type {CSSProperties} from "react";
import React, {useRef, useState} from "react";
import styled from "styled-components";
import type {Link} from "../../../../../data/models/Link";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../../utils/dom/DomUtils";
import {ReactUtils} from "../../../../utils/ReactUtils";
import type {TransportLayer} from "../../../../../data/TransportLayer";
import {XHRLoader} from "../../../../../utils/loader/XHRLoader";
import {useClickOutside} from "../../../utils";
import {InfoBubbleV5} from "../../../button/InfoBubbleV5";
import {SpaceItemContainerStyled} from "../../../spaceeditor/SpaceItemContainer.Style";
import {ConfirmWindowV5} from "../../../popup/ConfirmWindowV5";
import {baseDistance, ELLIPSIS, FlexCenterStyle, radius} from "../../../styles/styles";
import BreakLink from "../../../icons/unlink.svg?react";
import Trash from "../../../icons/trash.svg?react";
import {SpaceItemActionButtonsV5} from "../../../toolbar/SpaceItemActionButtonsV5";
import {XyiconFeature} from "../../../../../generated/api/base";
import CrossportfolioXyiconIcon from "../../../icons/cross-portfolio-xyicon.svg?react";
import {colorPalette} from "../../../styles/colorPalette";
import {breakLinks} from "./LinkLineActionBarV5";

interface ICrossPortfolioLinkData {
	link: Link;
	crossPortfolioXyiconId: string;
	otherPortfolioId: string;
	otherPortId: string;
	onePortId: string;
}

interface ICrossPortfolioXyiconProps {
	readonly transport: TransportLayer;
	readonly linkData: ICrossPortfolioLinkData;
	readonly portfolioId?: string;
	readonly showIconOnly?: boolean;
	readonly showBreakLinkButton: boolean;
	readonly showDeleteButton: boolean;
	readonly isOption?: boolean;
}

export const CrossPortfolioXyiconV5 = (props: ICrossPortfolioXyiconProps) => {
	const transport = props.transport;
	const {linkData, showIconOnly, showBreakLinkButton, showDeleteButton, isOption} = props;
	const parentRef = useRef<HTMLDivElement>(null);
	const contextMenuRef = useRef<HTMLDivElement>(null);
	const [isInfoBubbleVisible, setIsInfoBubbleVisible] = useState<boolean>(false);
	const [isContextMenuOpen, setIsContextMenuOpen] = useState<boolean>(false);

	useClickOutside([parentRef, contextMenuRef], () => {
		setIsContextMenuOpen(false);
	});

	const onContextMenu = (event: React.MouseEvent) => {
		if (showIconOnly) {
			event.preventDefault();
			setIsContextMenuOpen(true);
		}
	};

	const infoText = `UUID: ${linkData.crossPortfolioXyiconId}`;

	const transformForContextMenuRef =
		parentRef.current &&
		contextMenuRef.current &&
		DomUtils.getFixedFloatingElementPosition(parentRef.current, contextMenuRef.current, VerticalAlignment.bottomOuter, HorizontalAlignment.center);

	const inlineStyleForContextMenu: CSSProperties = {};

	if (transformForContextMenuRef) {
		inlineStyleForContextMenu.transform = transformForContextMenuRef.translate;
		inlineStyleForContextMenu.left = "0";
	}

	inlineStyleForContextMenu.visibility = isContextMenuOpen ? "visible" : "hidden";

	const onBreakLinkClickMaybe = showBreakLinkButton ? () => breakLinks(transport, [linkData.link.id], true, props.portfolioId) : undefined;
	const onDeleteClickMaybe = showDeleteButton
		? async () => {
				const isConfirmed = await ConfirmWindowV5.open("Are you sure you want to delete the selected 1 item?");

				if (isConfirmed) {
					return transport.requestForOrganization({
						url: "xyicons/delete?v=2.0",
						method: XHRLoader.METHOD_DELETE,
						params: {
							portfolioID: linkData.otherPortfolioId,
							xyiconIDList: [linkData.crossPortfolioXyiconId],
						},
					});
				}
			}
		: undefined;

	return (
		<CrossPortfolioXyiconStyled
			className={ReactUtils.cls("CrossPortfolioXyicon DraggableXyiconCatalogItem", {showIconOnly})}
			onMouseEnter={() => setIsInfoBubbleVisible(true)}
			onMouseLeave={() => setIsInfoBubbleVisible(false)}
			onContextMenu={onContextMenu}
		>
			<SpaceItemContainerStyled
				ref={parentRef}
				className="SpaceItemContainer hbox"
			>
				<div className={ReactUtils.cls("SpaceItem hbox alignCenter", {showIconOnly})}>
					<div className="thumbnailContainer flexCenter">
						<CrossportfolioXyiconIcon />
					</div>

					{!showIconOnly && (
						<>
							<div className="guid">{linkData.crossPortfolioXyiconId}</div>
							<SpaceItemActionButtonsV5
								deleteItem={onDeleteClickMaybe}
								isCrossPortfolioXyicon={true}
								onBreakLinkClick={!isOption && onBreakLinkClickMaybe}
								showDeleteButton={!isOption && showDeleteButton}
								showInfoButton={false}
								options={
									isOption && [
										{
											label: "Break Link",
											onClick: onBreakLinkClickMaybe,
											IconComponent: BreakLink,
										},
										{
											label: "Delete",
											onClick: onDeleteClickMaybe,
											IconComponent: Trash,
										},
									]
								}
								feature={XyiconFeature.Xyicon}
								isFavorite={false}
							/>
						</>
					)}
				</div>
				<InfoBubbleV5
					content={infoText}
					anchorRef={parentRef}
					hide={!isInfoBubbleVisible}
				/>
			</SpaceItemContainerStyled>
		</CrossPortfolioXyiconStyled>
	);
};

const CrossPortfolioXyiconStyled = styled.div`
	${FlexCenterStyle};

	.SpaceItem {
		flex-wrap: nowrap;
		gap: ${baseDistance.sm};
		width: 100%;

		.thumbnailContainer {
			width: 40px;
			height: 40px;
			position: relative;
			background-color: ${colorPalette.gray.c200Light};
			border-radius: ${radius.sm};
		}
	}

	.guid {
		${ELLIPSIS};
	}
`;
